import React, { useCallback, useState } from 'react';
import { createRoot, Root } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ScoreWidgetForm } from '@traquility/ui/diary';
import {
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem,
} from '../ui/common/Dropdown';
import type {
  ScoreWidgetFormData,
  ScoreWidgetFormLabels,
  ScoreWidgetFormContainerProps,
} from '../shared/types/scoreWidgetForm';

export type { ScoreWidgetFormContainerProps };

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 10,
    },
  },
});

const CREATE_SCORE_WIDGET_MUTATION = `
  mutation CreateScoreWidget($input: CreateScoreWidgetInput!) {
    createScoreWidget(input: $input) {
      scoreWidget {
        id
        name
      }
      errors
    }
  }
`;

const UPDATE_SCORE_WIDGET_MUTATION = `
  mutation UpdateScoreWidget($input: UpdateScoreWidgetInput!) {
    updateScoreWidget(input: $input) {
      scoreWidget {
        id
        name
      }
      errors
    }
  }
`;

interface DeleteModalProps {
  isOpen: boolean;
  onClose: () => void;
  onDelete: () => void;
  labels: ScoreWidgetFormLabels;
  isDeleting: boolean;
}

function DeleteModal({ isOpen, onClose, onDelete, labels: l, isDeleting }: DeleteModalProps) {
  if (!isOpen) return null;

  return (
    <div className="modal show d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">{l.deleteTitle}</h5>
            <button type="button" className="btn-close" onClick={onClose} disabled={isDeleting}></button>
          </div>
          <div className="modal-body">
            <p>{l.deleteConfirmation}</p>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={isDeleting}>
              {l.cancel}
            </button>
            <button
              type="button"
              className="btn btn-danger"
              onClick={onDelete}
              disabled={isDeleting}
            >
              {isDeleting ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                  {l.deleteButton}
                </>
              ) : (
                l.deleteButton
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ScoreWidgetFormContainerInner({
  graphqlEndpoint,
  csrfToken,
  initialData,
  backUrl,
  successUrl,
  editUrlTemplate,
  labels,
  errors: initialErrors = {},
  mode,
}: ScoreWidgetFormContainerProps) {
  const [errors, setErrors] = useState<Record<string, string[]>>(initialErrors);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleSubmit = useCallback(async (data: ScoreWidgetFormData) => {
    setIsSubmitting(true);
    setErrors({});

    const isEdit = mode === 'edit';
    const mutation = isEdit ? UPDATE_SCORE_WIDGET_MUTATION : CREATE_SCORE_WIDGET_MUTATION;

    const input: Record<string, unknown> = {
      name: data.name,
    };

    if (isEdit) {
      input.id = data.id;
    }

    try {
      const response = await fetch(graphqlEndpoint, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
        body: JSON.stringify({ query: mutation, variables: { input } }),
      });

      if (!response.ok) throw new Error('Network error');

      const result = await response.json();

      if (result.errors) {
        setErrors({ base: result.errors.map((e: { message: string }) => e.message) });
        setIsSubmitting(false);
        return;
      }

      const mutationResult = isEdit ? result.data.updateScoreWidget : result.data.createScoreWidget;

      if (mutationResult.errors?.length > 0) {
        setErrors({ base: mutationResult.errors });
        setIsSubmitting(false);
        return;
      }

      // For new records, redirect to edit page using the template
      if (!isEdit && editUrlTemplate && mutationResult.scoreWidget?.id) {
        window.location.href = editUrlTemplate.replace(':id', mutationResult.scoreWidget.id);
      } else {
        window.location.href = successUrl;
      }
    } catch {
      setErrors({ base: ['An unexpected error occurred. Please try again.'] });
      setIsSubmitting(false);
    }
  }, [graphqlEndpoint, csrfToken, successUrl, editUrlTemplate, mode]);

  const handleCancel = useCallback(() => {
    window.location.href = backUrl;
  }, [backUrl]);

  const handleDelete = useCallback(async () => {
    setIsDeleting(true);

    try {
      const response = await fetch(graphqlEndpoint, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
        body: JSON.stringify({
          query: `
            mutation DeleteScoreWidget($input: DeleteScoreWidgetInput!) {
              deleteScoreWidget(input: $input) {
                success
                errors
              }
            }
          `,
          variables: { input: { id: initialData.id } },
        }),
      });

      const result = await response.json();

      if (result.errors || result.data.deleteScoreWidget.errors?.length > 0) {
        const msgs = result.errors?.map((e: { message: string }) => e.message) ||
          result.data.deleteScoreWidget.errors;
        setErrors({ base: msgs });
        setIsDeleting(false);
        setShowDeleteModal(false);
        return;
      }

      window.location.href = backUrl;
    } catch {
      setErrors({ base: ['An unexpected error occurred. Please try again.'] });
      setIsDeleting(false);
    }
  }, [graphqlEndpoint, csrfToken, initialData.id, backUrl]);

  return (
    <>
      {mode === 'edit' && (
        <div className="position-absolute" style={{ top: 10, right: 10 }}>
          <Dropdown>
            <DropdownTrigger className="btn btn-link text-secondary">
              <i className="bi bi-three-dots"></i>
            </DropdownTrigger>
            <DropdownMenu align="end">
              <DropdownItem className="text-danger" onClick={() => setShowDeleteModal(true)}>
                {labels.delete}
              </DropdownItem>
            </DropdownMenu>
          </Dropdown>
        </div>
      )}

      <ScoreWidgetForm
        initialData={initialData}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        errors={errors}
        labels={labels}
        mode={mode}
        isSubmitting={isSubmitting}
      />

      <DeleteModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onDelete={handleDelete}
        labels={labels}
        isDeleting={isDeleting}
      />
    </>
  );
}

function ScoreWidgetFormContainer(props: ScoreWidgetFormContainerProps) {
  return (
    <QueryClientProvider client={queryClient}>
      <ScoreWidgetFormContainerInner {...props} />
    </QueryClientProvider>
  );
}

let formRoot: Root | null = null;

export function mountScoreWidgetForm(
  container: HTMLElement,
  props: ScoreWidgetFormContainerProps
): Root {
  if (formRoot) formRoot.unmount();
  const root = createRoot(container);
  formRoot = root;
  root.render(<ScoreWidgetFormContainer {...props} />);
  return root;
}

export function unmountScoreWidgetForm(): void {
  if (formRoot) {
    formRoot.unmount();
    formRoot = null;
  }
}

export default ScoreWidgetFormContainer;
